import { useMemo } from 'react';
import { stripLanguage } from '../i18n/languages';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  getLanguageRouteConfigByPath, 
  getAlternateLanguageUrls 
} from '../util/languageRoutes';
import type { RouteConfig } from '../util/routeConfig';

/**
 * Hook to get the localized route configuration for the current page
 */
export const useLocalizedRoute = (): {
  routeConfig: RouteConfig | null;
  alternateUrls: Array<{ lang: string; url: string }>;
  currentPath: string;
} => {
  const { currentLanguage } = useLanguage();
  const location = useLocation();

  const { routeConfig, currentPath, alternateUrls } = useMemo(() => {
    const normalizedPath = stripLanguage(location.pathname);

    // Get route config for current language
    const config = getLanguageRouteConfigByPath(currentLanguage, normalizedPath);

    // Get alternate URLs for hreflang
    const alternates = getAlternateLanguageUrls(normalizedPath);

    return {
      routeConfig: config,
      currentPath: normalizedPath,
      alternateUrls: alternates,
    };
  }, [currentLanguage, location.pathname]);

  return {
    routeConfig,
    alternateUrls,
    currentPath,
  };
};

